import { createLazyComponent } from '@/utils/lazy-loading';

// Enhanced lazy component creator with chunk naming
const createChunkedLazyComponent = (
  importFunc: () => Promise<any>,
  chunkName: string,
  componentName?: string
) => {
  return createLazyComponent(
    () => importFunc().then(mod => ({ default: mod.default })),
    componentName ?? chunkName
  );
};

// ===== PUBLIC ROUTES (Core website functionality) =====
export const Home = createChunkedLazyComponent(() => import('../../pages/home'), 'home');
export const Auth = createChunkedLazyComponent(() => import('../../pages/auth'), 'auth');
export const Treatments = createChunkedLazyComponent(
  () => import('../../pages/treatments'),
  'treatments'
);
export const TreatmentDetail = createChunkedLazyComponent(
  () => import('../../pages/treatment-detail'),
  'treatment-detail'
);
export const Providers = createChunkedLazyComponent(
  () => import('../../pages/providers'),
  'providers'
);
export const ProviderDetail = createChunkedLazyComponent(
  () => import('../../pages/provider-detail'),
  'provider-detail'
);
export const ClinicDetail = createChunkedLazyComponent(
  () => import('../../pages/clinic-detail'),
  'clinic-detail'
);
export const Doctors = createChunkedLazyComponent(() => import('../../pages/doctors'), 'doctors');
export const DoctorDetail = createChunkedLazyComponent(
  () => import('../../pages/doctor-detail'),
  'doctor-detail'
);
export const Equipments = createChunkedLazyComponent(
  () => import('../../pages/equipments'),
  'equipments'
);
export const EquipmentDetail = createChunkedLazyComponent(
  () => import('../../pages/equipment-detail'),
  'equipment-detail'
);
export const MedicalProcedureDetail = createChunkedLazyComponent(
  () => import('../../pages/medical-procedure-detail'),
  'medical-procedure-detail'
);

// ===== CONTENT ROUTES (Blog, info pages) =====
export const Blog = createChunkedLazyComponent(() => import('../../pages/blog'), 'blog');
export const BlogPost = createChunkedLazyComponent(
  () => import('../../pages/blog-post'),
  'blog-post'
);

// ===== DASHBOARDS (User/Partner functionality) =====
export const UserDashboard = createChunkedLazyComponent(
  () => import('../../pages/user-dashboard'),
  'user-dashboard'
);
export const PartnerDashboard = createChunkedLazyComponent(
  () => import('../../pages/partner-dashboard'),
  'partner-dashboard'
);

// ===== ADMIN ROUTES (Separate chunk for admin functionality) =====
export const AdminClinics = createChunkedLazyComponent(
  () => import(/* webpackChunkName: "admin-clinics" */ '../../pages/admin-clinics'),
  'admin-clinics'
);
export const AdminDashboard = createChunkedLazyComponent(
  () => import(/* webpackChunkName: "admin-dashboard" */ '../../pages/admin-dashboard'),
  'admin-dashboard'
);
// Note: BlogPost, UserDashboard, Providers, ProviderDetail are now defined in the appropriate sections above
// ===== UTILITY ROUTES (Maps, locations) =====
export const ProvidersLocation = createChunkedLazyComponent(
  () => import('../../pages/providers-location'),
  'providers-location'
);
export const Favorites = createChunkedLazyComponent(
  () => import('../../pages/favorites'),
  'favorites'
);

// ===== LEGAL & INFO PAGES (Static content) =====
export const PrivacyPolicy = createChunkedLazyComponent(
  () => import('../../pages/privacy-policy'),
  'privacy-policy'
);
export const TermsConditions = createChunkedLazyComponent(
  () => import('../../pages/terms-conditions'),
  'terms-conditions'
);
export const CookiePolicy = createChunkedLazyComponent(
  () => import('../../pages/cookie-policy'),
  'cookie-policy'
);
export const MedicalDisclaimer = createChunkedLazyComponent(
  () => import('../../pages/medical-disclaimer'),
  'medical-disclaimer'
);
export const Subprocessors = createChunkedLazyComponent(
  () => import('../../pages/subprocessors'),
  'subprocessors'
);
export const Changelog = createChunkedLazyComponent(
  () => import('../../pages/changelog'),
  'changelog'
);

// ===== SUPPORT PAGES =====
export const AntiDiscrimination = createChunkedLazyComponent(
  () => import('../../pages/anti-discrimination'),
  'anti-discrimination'
);
export const ComplaintResolution = createChunkedLazyComponent(
  () => import('../../pages/complaint-resolution'),
  'complaint-resolution'
);

// ===== SPECIAL PAGES =====
export const AboutUs = createChunkedLazyComponent(() => import('../../pages/about-us'), 'about-us');
export const HowItWorks = createChunkedLazyComponent(
  () => import('../../pages/how-it-works'),
  'how-it-works'
);
export const MedipathsLab = createChunkedLazyComponent(
  () => import('../../pages/MedipathsLab'),
  'medipaths-lab'
);
export const Glossary = createChunkedLazyComponent(
  () => import('../../pages/glossary'),
  'glossary'
);
export const AIChatbotPage = createChunkedLazyComponent(
  () => import('../../pages/ai-chatbot'),
  'ai-chatbot'
);
export const NotFound = createChunkedLazyComponent(
  () => import('../../pages/not-found'),
  'not-found'
);
export const ThankYou = createChunkedLazyComponent(
  () => import('../../pages/thank-you'),
  'thank-you'
);

// Pages object for page detection
export const PAGES = {
  Home,
  Auth,
  ClinicDetail,
  Treatments,
  Equipments,
  Blog,
  TreatmentDetail,
  AdminClinics,
  AdminDashboard,
  BlogPost,
  UserDashboard,
  PartnerDashboard,
  Providers,
  ProviderDetail,
  ProvidersLocation,
  PrivacyPolicy,
  TermsConditions,
  CookiePolicy,
  MedicalDisclaimer,
  AntiDiscrimination,
  ComplaintResolution,
  Doctors,
  DoctorDetail,
  MedicalProcedureDetail,
  EquipmentDetail,
  ThankYou,
  AboutUs,
  HowItWorks,
  MedipathsLab,
  Glossary,
  Subprocessors,
  AIChatbotPage,
  Favorites,
  NotFound,
  Changelog,
};
